import { useCallback, useState } from 'react';
import { usePlan, type Plan } from './usePlan';

/**
 * The canonical feature → required-plan mapping. Lives here so a single
 * change reverberates across every gated surface (travel mode, advanced
 * filters, view history, etc.).
 */
export type FeatureKey =
  | 'incognito'
  | 'travel'
  | 'advanced_filters'
  | 'profile_view_history'
  | 'last_active'
  | 'boost';

const REQUIRED: Record<FeatureKey, Plan> = {
  incognito:            'premium',
  travel:               'premium',
  advanced_filters:     'premium',
  profile_view_history: 'premium',
  last_active:          'premium',
  boost:                'premium', // boosts pack — also available a-la-carte
};

const RANK: Record<Plan, number> = { free: 0, premium: 1, vip: 2 };

export interface GateState {
  allowed: boolean;
  requiredPlan: Plan;
  upsellOpen: boolean;
  /** Open the upsell sheet for this feature. */
  prompt(): void;
  close(): void;
}

export const useFeatureGate = (feature: FeatureKey): GateState => {
  const { plan } = usePlan();
  const requiredPlan = REQUIRED[feature];
  const allowed = RANK[plan] >= RANK[requiredPlan];
  const [upsellOpen, setUpsellOpen] = useState(false);

  const prompt = useCallback(() => {
    if (!allowed) setUpsellOpen(true);
  }, [allowed]);

  const close = useCallback(() => setUpsellOpen(false), []);

  return { allowed, requiredPlan, upsellOpen, prompt, close };
};
